import { EvaStorage } from '@springtree/eva-sdk-core-storage';
import { EvaEndpoint, IEvaEndpointSerializedData } from './endpoint';
import { logger } from '@springtree/eva-sdk-core-logger';
import { getServiceSettings } from './settings';

/**
 * The storage key used to store the endpoints cache
 *
 * @export
 * @interface IEvaEndpointStorage
 */
export interface IEvaEndpointStorage {
  evaEndpointCache: string;
}

/**
 * This is the object stored in the above storage key.
 * After JSON parsing the string it should match this interface
 *
 * @export
 * @interface IEvaEndpointsCache
 */
export interface IEvaEndpointsCache {
  [endpointUrl: string]: IEvaEndpointSerializedData;
}

// The bootstrap cache (ie endpoint registry)
// To prevent excessive bootstrapping and to quickly be able to access
// configuration for an endpoint we will use this cached registry
//
const bootstrappedEndpoints: {[uri: string]: EvaEndpoint} = {};

/**
 * Helper method to check if an endpoint has been bootstrapped
 *
 * @export
 * @param {string} uri The EVA backend hostname
 * @param {number} [targetApplicationId] Optional check the endpoint is bootstrapped for a specific application id
 * @returns {boolean}
 */
export function hasEndPointBootstrapped(
  uri: string,
  targetApplicationId?: number,
): boolean {
  const endpoint = bootstrappedEndpoints[uri];
  if (targetApplicationId) {
    return !!endpoint && endpoint.currentApplicationId === targetApplicationId;
  }
  return !!endpoint;
}

/**
 * Required options for bootstrapping an endpoint
 *
 * @export
 * @interface IEvaBootstrapOptions
 */
export interface IEvaBootstrapOptions {
  /**
   * The EVA endpoint to bootstrap
   *
   * @type {string}
   */
  uri: string;

  /**
   * Optional target application id for EVA environments hosting more then one
   * application.
   * Default behavior of selecting the first application should suffice for
   * most EVA backend.
   * NOTE: A previously bootstrapped endpoint will switch to the new application
   *
   * @type {number}
   */
  targetApplicationId?: number;

  /**
   * Forces the bootstrap to be done even if the endpoint was previously
   * bootstrapped
   *
   * @type {boolean}
   */
  forceNewBootstrap?: boolean;

  /**
   * If provided the endpoint cache can be pre-heated from storage preventing
   * actual calls to the backend. Serialized data contains a bootstrapped timestamp.
   * Bootstrapped data is usually cached for 10 minutes
   *
   * @type {Storage}
   */
  storage?: EvaStorage<IEvaEndpointStorage>;
}

/**
 * This method will bootstrap an EVA endpoint or will return a previously
 * bootstrapped endpoints data.
 * The bootstrapped endpoint can be used with the EvaService class to make
 * calls the the EVA backend.
 * The EvaService class needs details from the bootstrap for configuration
 * and defaults
 *
 * @export
 * @param {IEvaBootstrapOptions} args
 * @returns
 */
export async function bootstrapEndpoint(
  args: IEvaBootstrapOptions,
): Promise<EvaEndpoint> {
  if (!args || !args.uri) {
    throw new Error('Missing required EVA endpoint uri');
  }

  const serviceSettings = getServiceSettings();

  if (args.storage) {
    // Fill the bootstrapped endpoints from storage
    //
    try {
      const storedEndpoints = JSON.parse(args.storage.getItem('evaEndpointCache') || '{}');
      for (const [endpointUrl, data] of Object.entries(storedEndpoints)) {
        const endpointData = data as IEvaEndpointSerializedData;

        bootstrappedEndpoints[endpointUrl] = new EvaEndpoint(endpointData);
      }
    } catch (storageError) {
      logger.warn('[EVA-BOOTSTRAP] Invalid stored endpoint cache', storageError);
    }
  }

  const now = +(new Date());
  const threshold = now - serviceSettings.endpointCacheDuration;

  if (args.forceNewBootstrap || !bootstrappedEndpoints[args.uri] || bootstrappedEndpoints[args.uri].bootstrappedAt < threshold) {
    const endpoint = new EvaEndpoint(args.uri);
    await endpoint.bootstrap(args.targetApplicationId);
    bootstrappedEndpoints[args.uri] = endpoint;

    // Update storage for future pre-heating
    //
    if (args.storage) {
      try {
        const storedEndpoints = JSON.parse(args.storage.getItem('evaEndpointCache') || '{}');
        storedEndpoints[args.uri] = endpoint.serialise();
        args.storage.setItem('evaEndpointCache', JSON.stringify(storedEndpoints));
      } catch (storageError) {
        logger.warn('[EVA-BOOTSTRAP] Failed to store endpoint cache', storageError);
      }
    }

    return endpoint;
  }

  const endpoint = bootstrappedEndpoints[args.uri];
  if (args.targetApplicationId && args.targetApplicationId !== endpoint.currentApplicationId) {
    endpoint.selectApplication(args.targetApplicationId);
  }
  return endpoint;
}
